// src/components/grammar/GrammarGuide.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  ChevronRight,
  ChevronLeft,
  BookOpen,
  Target,
  TrendingUp,
  Bookmark,
  BarChart3,
  AlertCircle,
  Award,
  CheckCircle,
  LayoutGrid
} from 'lucide-react';

const GrammarGuide = ({ isOpen, onClose, onComplete }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Guide steps configuration
  const steps = [
    {
      id: 'welcome',
      icon: <BookOpen className="w-12 h-12 text-blue-600" />,
      title: t('grammar.guide.welcome.title'),
      content: (
        <div className="space-y-4">
          <p className="text-gray-700 text-lg">
            {t('grammar.guide.welcome.description')}
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 font-medium">
              {t('grammar.guide.welcome.ready')}
            </p>
            <p className="text-blue-600 text-sm mt-1">
              {t('grammar.guide.welcome.subtitle')}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'lesson-types',
      icon: <Target className="w-12 h-12 text-purple-600" />,
      title: t('grammar.guide.lessonTypes.title'),
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.guide.lessonTypes.description')}
          </p>

          {/* Lesson Type Cards */}
          <div className="space-y-3">
            {/* Connected */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4 animate-slideInLeft">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">●●</span>
                </div>
                <h4 className="font-semibold text-blue-900">Connected</h4>
              </div>
              <p className="text-blue-800 text-sm">
                {t('grammar.guide.lessonTypes.connected')}
              </p>
            </div>

            {/* Lesson Only */}
            <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4 animate-slideInLeft animate-delay-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-white" />
                </div>
                <h4 className="font-semibold text-green-900">Lesson Only</h4>
              </div>
              <p className="text-green-800 text-sm">
                {t('grammar.guide.lessonTypes.lessonOnly')}
              </p>
            </div>

            {/* Practice Only */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4 animate-slideInLeft animate-delay-200">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                  <Target className="w-4 h-4 text-white" />
                </div>
                <h4 className="font-semibold text-orange-900">Practice Only</h4>
              </div>
              <p className="text-orange-800 text-sm">
                {t('grammar.guide.lessonTypes.practiceOnly')}
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-yellow-800 text-sm">
              💡 {t('grammar.guide.lessonTypes.tip')}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'floating-tab',
      icon: <LayoutGrid className="w-12 h-12 text-indigo-600" />,
      title: t('grammar.guide.floatingTab.title'),
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.guide.floatingTab.description')}
          </p>

          {/* Floating Tab Visualization */}
          <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border-2 border-dashed border-gray-300">
            <div className="absolute -right-2 top-1/2 -translate-y-1/2 flex flex-col gap-2 animate-pulseGlow">
              <div className="w-12 h-12 bg-white rounded-lg shadow-lg flex items-center justify-center border-2 border-blue-400">
                <LayoutGrid className="w-6 h-6 text-blue-600" />
              </div>
              <div className="w-12 h-12 bg-white rounded-lg shadow-lg flex items-center justify-center border-2 border-red-400">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="w-12 h-12 bg-white rounded-lg shadow-lg flex items-center justify-center border-2 border-yellow-400">
                <Bookmark className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="w-12 h-12 bg-white rounded-lg shadow-lg flex items-center justify-center border-2 border-green-400">
                <BarChart3 className="w-6 h-6 text-green-600" />
              </div>
            </div>

            <div className="pr-16">
              <p className="text-gray-600 text-center text-sm">
                Your grammar content appears here
              </p>
            </div>
          </div>

          {/* Tab Descriptions */}
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <LayoutGrid className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.floatingTab.overview')}</p>
            </div>
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.floatingTab.mistakes')}</p>
            </div>
            <div className="flex items-start gap-2">
              <Bookmark className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.floatingTab.bookmarks')}</p>
            </div>
            <div className="flex items-start gap-2">
              <BarChart3 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.floatingTab.statistics')}</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-800 text-sm">
              📱 {t('grammar.guide.floatingTab.mobile')}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'mistakes',
      icon: <AlertCircle className="w-12 h-12 text-red-600" />,
      title: t('grammar.guide.mistakes.title'),
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.guide.mistakes.description')}
          </p>

          {/* Mock Mistake Card */}
          <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 animate-bounceIn">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-red-900">Modalverben</span>
              <span className="text-xs text-red-600">3 mistakes</span>
            </div>
            <p className="text-sm text-red-800 mb-2">
              Question: "Er ____ gut Deutsch sprechen."
            </p>
            <div className="flex gap-2 text-xs">
              <span className="px-2 py-1 bg-red-200 text-red-800 rounded">Your: muss</span>
              <span className="px-2 py-1 bg-green-200 text-green-800 rounded">Correct: kann</span>
            </div>
          </div>

          {/* Features List */}
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.mistakes.features')}</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.mistakes.review')}</p>
            </div>
            <div className="flex items-start gap-2">
              <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.mistakes.mark')}</p>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-3">
            <p className="text-green-800 text-sm font-medium">
              {t('grammar.guide.mistakes.tip')}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'bookmarks',
      icon: <Bookmark className="w-12 h-12 text-yellow-600" />,
      title: t('grammar.guide.bookmarks.title'),
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.guide.bookmarks.description')}
          </p>

          {/* Mock Bookmark Visual */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-yellow-900">Sample Question</span>
              <button className="p-1 hover:bg-yellow-100 rounded transition-colors">
                <Bookmark className="w-5 h-5 text-yellow-600 fill-yellow-600" />
              </button>
            </div>
            <p className="text-sm text-yellow-800">
              Click the bookmark icon to save challenging questions for later review
            </p>
          </div>

          {/* How-to Steps */}
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
              <p className="text-gray-700">{t('grammar.guide.bookmarks.how')}</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
              <p className="text-gray-700">{t('grammar.guide.bookmarks.access')}</p>
            </div>
            <div className="flex items-start gap-2">
              <span className="flex-shrink-0 w-6 h-6 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
              <p className="text-gray-700">{t('grammar.guide.bookmarks.remove')}</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-blue-800 text-sm">
              📌 {t('grammar.guide.bookmarks.tip')}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'progress',
      icon: <TrendingUp className="w-12 h-12 text-green-600" />,
      title: t('grammar.guide.progress.title'),
      content: (
        <div className="space-y-4">
          <p className="text-gray-700">
            {t('grammar.guide.progress.description')}
          </p>

          {/* Mock Progress Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-2xl font-bold text-blue-600">75%</p>
              <p className="text-xs text-blue-800">Accuracy</p>
            </div>
            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-2xl font-bold text-green-600">24</p>
              <p className="text-xs text-green-800">Exercises Done</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
              <p className="text-2xl font-bold text-purple-600">45m</p>
              <p className="text-xs text-purple-800">Time Spent</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-200">
              <p className="text-2xl font-bold text-orange-600">7</p>
              <p className="text-xs text-orange-800">Day Streak</p>
            </div>
          </div>

          {/* Features List */}
          <div className="space-y-2 text-sm">
            <div className="flex items-start gap-2">
              <BarChart3 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.progress.features')}</p>
            </div>
            <div className="flex items-start gap-2">
              <Award className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.progress.stats')}</p>
            </div>
            <div className="flex items-start gap-2">
              <Target className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.progress.weak')}</p>
            </div>
            <div className="flex items-start gap-2">
              <ChevronRight className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-gray-700">{t('grammar.guide.progress.continue')}</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'tips',
      icon: <Award className="w-12 h-12 text-purple-600" />,
      title: t('grammar.guide.tips.title'),
      content: (
        <div className="space-y-4">
          {/* Tips List */}
          <div className="space-y-3">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-l-4 border-blue-500 rounded-lg p-3 animate-slideInLeft">
              <p className="text-blue-900 text-sm font-medium">
                {t('grammar.guide.tips.consistency')}
              </p>
            </div>
            <div className="bg-gradient-to-r from-red-50 to-red-100 border-l-4 border-red-500 rounded-lg p-3 animate-slideInLeft animate-delay-100">
              <p className="text-red-900 text-sm font-medium">
                {t('grammar.guide.tips.mistakes')}
              </p>
            </div>
            <div className="bg-gradient-to-r from-yellow-50 to-yellow-100 border-l-4 border-yellow-500 rounded-lg p-3 animate-slideInLeft animate-delay-200">
              <p className="text-yellow-900 text-sm font-medium">
                {t('grammar.guide.tips.bookmarks')}
              </p>
            </div>
            <div className="bg-gradient-to-r from-green-50 to-green-100 border-l-4 border-green-500 rounded-lg p-3 animate-slideInLeft animate-delay-300">
              <p className="text-green-900 text-sm font-medium">
                {t('grammar.guide.tips.lessons')}
              </p>
            </div>
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 border-l-4 border-purple-500 rounded-lg p-3 animate-slideInLeft animate-delay-500">
              <p className="text-purple-900 text-sm font-medium">
                {t('grammar.guide.tips.statistics')}
              </p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'success',
      icon: <CheckCircle className="w-12 h-12 text-green-600" />,
      title: t('grammar.guide.success.title'),
      content: (
        <div className="space-y-4">
          <div className="text-center py-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounceIn">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <p className="text-gray-700 text-lg mb-4">
              {t('grammar.guide.success.description')}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-blue-800 text-sm">
                💡 {t('grammar.guide.success.reminder')}
              </p>
            </div>
          </div>

          {/* Celebration Animation */}
          <div className="text-center space-y-2">
            <div className="flex justify-center gap-2">
              <span className="text-3xl animate-bounce">🎉</span>
              <span className="text-3xl animate-bounce animate-delay-100">🎯</span>
              <span className="text-3xl animate-bounce animate-delay-200">📚</span>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const currentStepData = steps[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const handleNext = () => {
    if (!isLastStep) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    // Always save preference so guide doesn't show again automatically
    // The "don't show again" checkbox is for explicit user preference
    if (onComplete) {
      onComplete(dontShowAgain); // Pass the checkbox state
    }
    onClose();
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 pb-16 sm:pb-4 vocabulary-guide-modal animate-fade-in-fast">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Content - Mobile: bottom-sheet style, full width on small screens */}
      <div className="relative w-full max-w-2xl bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden vocabulary-guide-content animate-scale-in max-h-[92vh] sm:max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-4 sm:p-6 flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl sm:text-2xl md:text-3xl font-bold flex-1 min-w-0">
              {t('grammar.guide.title')}
            </h2>
            <button
              onClick={onClose}
              className="p-2.5 hover:bg-white/20 rounded-xl transition-colors touch-manipulation min-h-[48px] min-w-[48px] flex items-center justify-center flex-shrink-0"
              aria-label="Close guide"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <p className="text-blue-100 text-xs sm:text-sm md:text-base mt-1">
            {t('grammar.guide.subtitle')}
          </p>
        </div>

        {/* Progress Steps - Similar to VocabularyGuide */}
        <div className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">
              {t('grammar.guide.progress', { current: currentStep + 1, total: steps.length }) || `Step ${currentStep + 1} of ${steps.length}`}
            </span>
            <span className="text-sm text-gray-600">
              {Math.round(((currentStep + 1) / steps.length) * 100)}%
            </span>
          </div>

          {/* Step Indicators */}
          <div className="flex items-center gap-2">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className="flex-1 h-2 rounded-full overflow-hidden bg-gray-300 transition-all duration-300"
              >
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    index < currentStep
                      ? 'bg-gradient-to-r from-green-500 to-green-600 w-full'
                      : index === currentStep
                      ? 'bg-gradient-to-r from-blue-500 to-purple-500 w-full'
                      : 'bg-gray-300 w-0'
                  }`}
                  style={{
                    width: index <= currentStep ? '100%' : '0%'
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Content Area - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-8 min-h-0">
          <div className="space-y-4 sm:space-y-6">
            {/* Icon */}
            <div className="flex justify-center animate-bounceIn">
              {currentStepData.icon}
            </div>

            {/* Title */}
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 text-center animate-fadeIn">
              {currentStepData.title}
            </h3>

            {/* Content */}
            <div className="animate-fadeIn">
              {currentStepData.content}
            </div>
          </div>
        </div>

        {/* Footer Actions - Mobile: touch targets 48px, checkbox on every step */}
        <div className="bg-gray-50 border-t border-gray-200 p-4 sm:p-6 flex-shrink-0 pb-[env(safe-area-inset-bottom)]">
          {/* Don't Show Again Checkbox - visible on every step */}
          <label className="flex items-center gap-3 mb-4 cursor-pointer touch-manipulation min-h-[48px]" style={{ minHeight: '48px' }}>
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="w-5 h-5 sm:w-5 sm:h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 flex-shrink-0"
            />
            <span className="text-sm sm:text-base text-gray-600">
              {t('grammar.guide.dontShowAgain')}
            </span>
          </label>

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between gap-3">
            <button
              onClick={handlePrevious}
              disabled={isFirstStep}
              className={`flex items-center gap-2 px-4 py-3 sm:py-2 rounded-xl font-medium transition-all touch-manipulation min-h-[48px] ${
                isFirstStep
                  ? 'text-gray-400 cursor-not-allowed'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="hidden sm:inline">{t('grammar.guide.previous')}</span>
            </button>

            {isLastStep ? (
              <button
                onClick={handleFinish}
                className="flex items-center gap-2 px-5 sm:px-6 py-3 min-h-[48px] bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 touch-manipulation"
              >
                <CheckCircle className="w-5 h-5" />
                {t('grammar.guide.startLearning')}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="flex items-center gap-2 px-5 sm:px-6 py-3 min-h-[48px] bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 touch-manipulation"
              >
                {t('grammar.guide.next')}
                <ChevronRight className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GrammarGuide;